import { useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';

import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import ToggleButton from 'react-bootstrap/ToggleButton';
import ToggleButtonGroup from 'react-bootstrap/ToggleButtonGroup';
import Spinner from 'react-bootstrap/Spinner';

import { updateUser, requestPasswordReset } from '@utils/api';
import { getRequestResetErrorMessage } from '@utils/passwordResetErrorCopy';
import { useAuth } from '@contexts/User.context';

import AdminView from '@components/layout/views/AdminView';
import Input from '@components/form/Input';
import Alert from '@components/alert/Alert';

/**
 * Edit profile page
 */
const Profile = () => {
	const { currentUser, refetchCurrentUser } = useAuth();
	const { measurement_system: measurementSystem } = currentUser ?? {};

	const { control, handleSubmit, setFocus } = useForm({
		defaultValues: {
			name: currentUser.name ?? '',
			email: currentUser.email ?? '',
			measurementSystem: measurementSystem ?? 'metric',
		},
	});

	const {
		mutateAsync: updateUserMutation,
		isSuccess: userUpdatedSuccess,
		isError: userUpdatedError,
		isPending: userUpdatedPending,
	} = useMutation({
		mutationFn: updateUser,
		onSuccess: refetchCurrentUser,
	});

	const passwordResetMutation = useMutation({
		mutationFn: () => requestPasswordReset(currentUser?.email),
	});

	const handlePasswordReset = event => {
		event.preventDefault();
		passwordResetMutation.mutate();
	};

	const onSubmit = values => {
		const { name, measurementSystem } = values;
		updateUserMutation({ name, measurementSystem });
	};

	useEffect(() => {
		if (!currentUser?.name) {
			setFocus('name');
		}
	}, [setFocus]);

	return (
		<AdminView maxWidth={7} title="Profile">
			{userUpdatedSuccess && (
				<Alert variant="success" heading="User updated">
					<p>Your user profile has been updated.</p>
				</Alert>
			)}

			{userUpdatedError && (
				<Alert variant="danger" heading="Error updating user">
					<p>Something went wrong and your user profile could not be updated.</p>
				</Alert>
			)}

			{!currentUser?.name && (
				<Alert variant="warning" heading="Missing name">
					<p>
						Your user profile is missing a name. Please update your profile to include
						your name.
					</p>
				</Alert>
			)}

			<Form onSubmit={handleSubmit(onSubmit)}>
				<Controller
					className="mb-3"
					name="name"
					control={control}
					render={({ field }) => (
						<Input
							{...field}
							type="text"
							placeholder="Enter name"
							label="Name"
							autoComplete="name"
							required
						/>
					)}
				/>
				<Controller
					className="mb-3"
					name="email"
					control={control}
					render={({ field }) => (
						<Input
							{...field}
							type="email"
							placeholder="Enter email"
							label="Email"
							disabled
							required
						/>
					)}
				/>

				{/* For some reason bootstrap does not handle select keyboard inputs
				(space to select) also without the disabled prop there is an invisible
				checkbox that makes tabbing extremely annoying */}
				<p className="mt-3 mb-2 text-muted">Measurement System</p>
				<Controller
					className="mb-3"
					name="measurementSystem"
					control={control}
					render={({ field }) => (
						<ToggleButtonGroup
							{...field}
							type="radio"
							name="measurement-system"
							className="d-flex"
							onChange={value => field.onChange(value)}>
							{['metric', 'imperial'].map(value => (
								<ToggleButton
									key={`radio-button-${value}`}
									id={`radio-button-${value}`}
									value={value}
									// disabled={field?.value == value}
									onKeyDown={({ key }) => key == ' ' && field.onChange(value)}
									variant={field?.value == value ? 'success' : 'dark'}>
									{value.charAt(0).toUpperCase() + value.slice(1)}
								</ToggleButton>
							))}
						</ToggleButtonGroup>
					)}
				/>

				<hr className="mb-5" />

				<Button type="submit" variant="success" disabled={userUpdatedPending}>
					Update User
				</Button>
				<Form.Group className="mb-3 mt-5" controlId="formResetPassword">
					<Form.Label>Password</Form.Label>
					<div>
						<Button
							variant="link p-0"
							onClick={handlePasswordReset}
							disabled={
								passwordResetMutation.isPending || passwordResetMutation.isSuccess
							}>
							Request password reset{' '}
							{passwordResetMutation.isPending && (
								<Spinner size="sm" className="mx-2" />
							)}
						</Button>
						{passwordResetMutation.isSuccess && (
							<>
								<p>
									Password reset request received and an email has been sent to{' '}
									<strong>{currentUser.email}</strong>.
								</p>
								<p className="mb-0">
									Check your email and follow the instructions.
								</p>
							</>
						)}
						{passwordResetMutation.isError && (
							<p className="text-danger mt-2">
								{getRequestResetErrorMessage(passwordResetMutation.error)}
							</p>
						)}
					</div>
				</Form.Group>
			</Form>
		</AdminView>
	);
};

export default Profile;
